import type { DestinationAccess } from "~/helpers/aiChat/canOpenDestination";
import type { FlagKey } from "~/hooks/useFeatureFlag";

import { useContext, useMemo } from "react";

import { ROLES } from "~/@types/roles";
import { AuthContext } from "~/auth/AuthProvider";
import { canViewOwnCommissions, getCompanySettingsAccess, hasRestockOrdererAccess } from "~/auth/roleChecks";
import useMyUser from "~/auth/useMyUser";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { useGetAllFeatureFlagsQuery } from "~/gql/queries/featureFlag.generated";
import canOpenDestination from "~/helpers/aiChat/canOpenDestination";
import useMyParentCompany from "~admin/parent-company/hooks/useMyParentCompany";

const useCanOpenDestination = (): ((path: string) => boolean) => {
	const { currentUser, isCustomer } = useContext(AuthContext);
	const { myUser } = useMyUser();
	const { data: flags } = useGetAllFeatureFlagsQuery(undefined, { skip: isCustomer });
	const { parentCompany } = useMyParentCompany();

	return useMemo(() => {
		const access: DestinationAccess = {
			canOrderRestock: hasRestockOrdererAccess(currentUser, myUser?.roles),
			canViewOwnCommissions: canViewOwnCommissions(myUser?.roles),
			companySettings: getCompanySettingsAccess(currentUser, myUser?.roles),
			hasParentCompany: parentCompany !== null,
			isFlagEnabled: (flag: FeatureFlagName) => flags?.[flag as FlagKey]?.enabled ?? false,
			isSuperAdmin: currentUser?.roles?.includes(ROLES.SUPER_ADMIN) ?? false
		};
		return (path: string) => canOpenDestination(path, access);
	}, [currentUser, flags, myUser?.roles, parentCompany]);
};

export default useCanOpenDestination;
